<template>
	<div>
		<card xs-tile sm-tile>

			<alert v-if="participationStore.authPermissions.order?.requires_payment_action" type="warning"
				   :content="participationStore.authPermissions.order.payable_until ? $t('text.order.payment_due', {date: $date(participationStore.authPermissions.order.payable_until).format('LLL')}) : $t('text.order.payment_failed')"
				   :button="$t('action.pay_now')" :to="$web('orders.pay', participationStore.authPermissions.order._key)"
				   :button-icon="false"
				   flat tile/>

			<alert v-if="participationStore.participation && !participationStore.participation.user?.is_account && participationStore.userToken"
				   :content="$t('text.participation.link_account')"
				   :button="$t('action.link_account')" type="warning" flat tile
				   :to="{path: '/assign', query: {users: participationStore.userToken, redirect: $route.fullPath}}"/>

			<div v-if="eventStore.isCompetition" id="results" class="divided">
				<card tile flat no-spacing
					  :to="$web('events.participations.show.results', [eventStore.event._key, participationStore.participation._key])">
					<card-title>
						<div class="w-full flex justify-between items-center">
							<span>{{ $t('label.results') }}</span>
							<span class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}">{{ $t('action.show_details') }}</span>
						</div>
					</card-title>
					<card-text>

						<missing-info v-if="!participationStore.hasStarted" :text="$t('missing.participations.results_not_started')"/>
						<missing-info
							v-else-if="liveTime === null && participationStore.participation.time === null && participationStore.participation.distance === null && participationStore.participation.points === null && !participationStore.participation.ranks.length"
							:text="$t('missing.results.index')"/>
						<description-list v-else>
							<description-list-item v-for="rank in participationStore.participation.ranks" :key="rank.id" :text-space="$device.isMobile ? 2/5 : 3/5" center>
								<template #title>
					<span class="flex flex-column">
						<span class="font-bold text-sm truncate">{{ rank.list_name }}</span>
						<span class="text-3xs truncate">{{ rank.list_group_name }}</span>
					</span>
								</template>
								<template #text>
									<div class="flex items-center">
										<chip :color="getColorForRank(rank.rank)" :icon="icons.rank" small>
									<span v-if="rank.primary_rank_value && participationStore.participation[rank.primary_rank_value_prop] !== rank.primary_rank_value"
										  class="flex flex-column">
										<span class="font-bold text-3xs leading-tight">{{ rank.rank }}. {{ $t('label.rank') }}</span>
										<span class="text-4xs">{{ participationStore.parseRankValue(rank.primary_rank_value, rank.primary_rank_value_prop) }}</span>
									</span>
											<span v-else class="font-bold">{{ rank.rank }}. {{ $t('label.rank') }}</span>
										</chip>
										<tooltip v-if="!rank.is_confirmed" :text="$t('text.temporary_results')" with-touch>
											<icon :icon="icons.warning" small color="warning"/>
										</tooltip>
									</div>
								</template>
							</description-list-item>

							<description-list-item v-if="liveTime !== null" :title="$t('enum.rank_metric.time.title')" :text="liveTime"/>
							<description-list-item v-else-if="participationStore.participation.time !== null" :title="$t('enum.rank_metric.time.title')"
												   :text="participationStore.parseRankValue(participationStore.participation.time, 'time', true)"/>
							<description-list-item v-if="participationStore.participation.distance !== null" :title="$t('enum.rank_metric.distance.title')"
													:text="participationStore.parseRankValue(participationStore.participation.distance, 'distance', true)"/>
							<description-list-item v-if="participationStore.participation.rounds" :title="$t('enum.rank_metric.rounds.title')"
												   :text="participationStore.parseRankValue(participationStore.participation.rounds, 'rounds', true)"/>
							<description-list-item v-if="participationStore.participation.altitude !== null" :title="$t('enum.rank_metric.altitude.title')"
												   :text="participationStore.parseRankValue(participationStore.participation.altitude, 'altitude', true)"/>
							<description-list-item v-if="participationStore.participation.points !== null" :title="$t('enum.rank_metric.points.title')"
												   :text="participationStore.parseRankValue(participationStore.participation.points, 'points', true)"/>

							<description-list-item v-if="participationStore.participation.pace && participationStore.participationPaceLabel"
												   :title="`${$t('label.pace')} Ø`"
												   :text="`${participationStore.participation.pace} ${participationStore.participationPaceLabel}`"/>

							<description-list-item :title="$t('label.status')">
								<template #text>
									<chip :color="participationStore.participation.status.color" small
										  :help="participationStore.participation.sub_status && participationStore.participation.sub_status.description ? participationStore.participation.sub_status.description : participationStore.participation.status.description">
										{{ participationStore.participation.status.title }}
									</chip>
								</template>
							</description-list-item>

						</description-list>
					</card-text>
				</card>

				<user-content v-if="participationStore.participation.result_info" :text="participationStore.participation.result_info" class="px-16 md:px-24 mb-16"/>
			</div>

			<card id="tracking" v-if="safeParticipationPolyline?.length || participationPolylinePending || participationPolylineError" tile flat no-spacing class="divided">
				<card-title>
					<div class="w-full flex justify-between items-center">
						<span>Tracking</span>
						<nuxt-link class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}" :to="$web('events.participations.show.tracking', [eventStore.event._key, participationStore.participation._key])">{{ $t('action.show_details') }}</nuxt-link>
					</div>
				</card-title>
				<card-text>
					<fetch-loader v-if="participationPolylinePending || navigatingToTrackingPage || participationPolylineError" :pending="participationPolylinePending || navigatingToTrackingPage" :error="participationPolylineError" @retry="refreshParticipationPolyline" height="160"/>
					<map-map v-else-if="safeParticipationPolyline" :focus-elements="safeParticipationPolyline" hide-controls rounded cursor="pointer"
							 @click="navigateToTrackingPage"
							 :controllable="false" class="h-160">
						<map-polyline :lat-lngs="safeParticipationPolyline" :color="eventStore.colorStyle"/>
					</map-map>
				</card-text>
			</card>

			<card id="about" tile flat no-spacing class="divided">
				<card-title>
					<div class="w-full flex justify-between items-center">
						<span>{{ $t('label.about_name', {name: participationStore.participation.first_name}) }}</span>
						<nuxt-link v-if="participationStore.editable" class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}"
								   :to="$web('events.participations.edit', [eventStore.event._key, participationStore.participation._key])">{{ $t('action.edit') }}
						</nuxt-link>
					</div>
				</card-title>
				<card-text>
					<description-list>

						<description-list-item v-if="participationStore.participation.ageClass" :title="$t('label.age_class')" :text="participationStore.participation.ageClass.name"/>
						<description-list-item v-if="participationStore.participation.age" :title="$t('label.age')" :text="participationStore.participation.age"/>

						<description-list-item v-if="participationStore.participation.gender" :title="$t('label.gender')"
											   :text="participationStore.participation.gender === 'm' ? $t('label.male') : $t('label.female')"/>

						<description-list-item v-if="participationStore.participation.club"
											   :title="[3288, 3287, 3590, 3664, 4240].includes(eventStore.event.id) ? 'Firma / Schule / Verein' : $t('label.club_company')">
							<template #text>
								<nuxt-link v-if="participationStore.participation.club._key" class="flex items-center"
										   :to="$web('clubs.show', participationStore.participation.club._key)"
										   :style="{color: participationStore.participation.club.color ?? $config.public.colors.primary}">
									<avatar :model="participationStore.participation.club" :size="24" class="mr-8"/>
									<span>{{ participationStore.participation.club.display_name }}</span>
								</nuxt-link>
								<span v-else>{{ participationStore.participation.club.display_name }}</span>
							</template>
						</description-list-item>

						<description-list-item v-if="!participationStore.participation.private && participationStore.participation.country" :title="$t('label.origin')">
							<template #text>
								<div class="flex items-center">
									<avatar class="mr-8" :size="24" :src="participationStore.participation.country.flag"/>
									<span>{{
											participationStore.participation.city ? `${participationStore.participation.city}, ` : null
										}}{{ participationStore.participation.country.locale_name }}</span>
								</div>
							</template>
						</description-list-item>


					</description-list>

					<lazy-profile-preview v-if="!participationStore.participation.private && participationStore.participation.user" :model="participationStore.participation.user" outlined
										  class="mt-16">
						<template #actions>
							<div class="flex flex-column items-center justify-center">
								<icon :icon="icons.next" large color="gray-400"/>
								<span class="text-4xs">{{ $t('action.show_profile') }}</span>
							</div>
						</template>
					</lazy-profile-preview>
				</card-text>
			</card>

			<card id="team" v-if="participationStore.participation.team" tile flat no-spacing class="divided">
				<card-title>
					<div class="w-full flex justify-between items-center">
						<span>{{ participationStore.participation.participationClass.team_settings?.label ?? $t('label.squad') }}</span>
						<div v-if="participationStore.canViewSensitiveInformation" class="text-sm font-regular font-text cursor-pointer" :style="{color: eventStore.colorStyle}" @click="isTeamInviteModalVisible = true">{{ $t('action.invite') }}
						</div>
					</div>
				</card-title>
				<card-text>
					<description-list>

						<description-list-item :title="$t('label.name')" :text="participationStore.participation.team.name"/>
						<description-list-item :title="$t('label.gender')">
							<template #text>
								<span v-if="participationStore.participation.team.gender === 'm'">{{ $t('label.male') }}</span>
								<span v-else-if="participationStore.participation.team.gender === 'f'">{{ $t('label.female') }}</span>
								<span v-else>{{ $t('label.mixed_gender') }}</span>
							</template>
						</description-list-item>
						<description-list-item :title="$t('label.type')">
							<template #text>
								<chip v-if="!participationStore.participation.team.public" color="warning" small clickable :help="$t('text.team.private', {team: participationStore.participation.team.name})" @click="isTeamInviteModalVisible = true">
									{{ $t('label.private_team') }}
								</chip>
								<chip v-else color="success" small clickable :help="$t('text.team.public', {team: participationStore.participation.team.name})" @click="isTeamInviteModalVisible = true">
									{{ $t('label.public_team') }}
								</chip>
							</template>
						</description-list-item>

						<description-list-item :title="$t('label.participants')">
							<template #text>
								<span v-if="participationStore.participation.participationClass.team_settings.min !== participationStore.participation.participationClass.team_settings.max">{{ participationStore.participation.team.participations.length }} / {{ participationStore.participation.participationClass.team_settings.min ?? 0 }} - {{ participationStore.participation.participationClass.team_settings.max ?? '∞' }}</span>
								<span v-else-if="participationStore.participation.participationClass.team_settings.max">{{ participationStore.participation.team.participations.length }} / {{ participationStore.participation.participationClass.team_settings.max ?? '∞' }}</span>
								<span v-else>{{ participationStore.participation.team.participations.length }}</span>
							</template>
						</description-list-item>

					</description-list>

					<client-only>
						<alert v-if="participationStore.canViewSensitiveInformation && participationStore.participation.participationClass.team_settings.min && participationStore.participation.participationClass.team_settings.min > participationStore.participation.team.participations.length"
							   type="warning" text
							   :button="$t('action.invite')"
							   :content="$t('text.team.min', {count: participationStore.participation.participationClass.team_settings.min - participationStore.participation.team.participations.length})"
							   class="mt-4" @click="isTeamInviteModalVisible = true"/>
					</client-only>

					<row v-if="participationStore.participation.team.participations.length" tight class="mt-16">
						<column v-for="teamParticipation in participationStore.participation.team.participations" :key="teamParticipation.id" cols="6" sm="3" md="2" lg="3">
							<participation-card :event="eventStore.event" :participation="teamParticipation" outlined />
						</column>
					</row>

				</card-text>
			</card>

			<card id="blog" v-if="participationStore.participation.articles?.length" tile flat no-spacing class="divided">
				<card-title>
					<div class="w-full flex justify-between items-center">
						<span>{{ $t('label.articles_about_the_participation') }}</span>
						<nuxt-link v-if="$auth.loggedIn" class="text-sm font-regular font-text flex items-center"
							 :to="{path: $web('my.profile.manage.articles.create'), query: {links: [`${ModelType.Event}-${eventStore.event.id}`]}}"
							 :style="{color: eventStore.colorStyle}" >
							<icon :icon="icons.create" class="mr-4" small/>
							<span>{{ $t('action.write_blog_article') }}</span>
						</nuxt-link>
					</div>
				</card-title>
				<card-text>
					<div class="mx--16 md:mx-0">
						<slider :show-arrows="$device.isDesktop" visible-arrows full-width-spaced>
							<slider-item v-for="(article, articleIndex) in participationStore.participation.articles" :key="article.id" :value="articleIndex">
								<article-card :article="article" class="article-card w-304" outlined/>
							</slider-item>
						</slider>
					</div>
				</card-text>
			</card>

			<card id="event" tile flat no-spacing class="divided">
				<card-title>{{ $t('label.event_information') }}</card-title>

				<card-text>
					<description-list>
						<description-list-item :title="$t('label.event')" :text="eventStore.event.display_name" :to="$web('events.show', [eventStore.event._key])"
											   :color="eventStore.colorStyle"/>
						<description-list-item v-if="participationStore.participation.stageRaces.length" :title="$t('label.stage_race')"
											   :text="participationStore.participation.stageRaces.map(stageRace => stageRace.name).join(', ')"
											   :to="$web('events.stage_races.show', [eventStore.event._key, participationStore.participation.stageRaces[0]._key])"
											   :color="eventStore.colorStyle"/>
						<description-list-item :title="$t('label.category')" :text="participationStore.participation.participationClass.name"
											   :to="$web('events.participation_classes.show', [eventStore.event._key, participationStore.participation.participationClass._key])"
											   :color="eventStore.colorStyle"/>
						<description-list-item :title="$t('label.location')">
							<template #text>
								<nuxt-link :to="$web('events.participations.show.map', [eventStore.event._key, participationStore.participation._key])"
										   :style="{color: eventStore.colorStyle}">
									<span>{{ eventStore.event.full_address }}</span>
								</nuxt-link>
							</template>
						</description-list-item>
						<description-list-item :title="$t('label.start_date')" :text="$date(participationStore.participation.participationClass.start_date).format('LLLL')"/>
						<description-list-item :title="$t('label.end_date')" :text="$date(participationStore.participation.participationClass.end_date).format('LLLL')"/>
						<description-list-item
							v-if="participationStore.participation.participationClass.virtualSettings && (participationStore.participation.participationClass.virtualSettings.start_time || participationStore.participation.participationClass.virtualSettings.end_time)"
							:title="$t('label.start_window')"
							:text="`${participationStore.participation.participationClass.virtualSettings.start_time ? $date().time(participationStore.participation.participationClass.virtualSettings.start_time).format('HH:mm') : $date().startOf('day').format('HH:mm')} Uhr - ${participationStore.participation.participationClass.virtualSettings.end_time ? $date().time(participationStore.participation.participationClass.virtualSettings.end_time).format('HH:mm') : $date().endOf('day').format('HH:mm')} Uhr`"/>
					</description-list>
				</card-text>
			</card>

			<card id="details" tile flat no-spacing class="divided">
				<card-title>{{ $t('label.participation_details') }}</card-title>
				<card-text>
					<description-list>
						<description-list-item :title="$t('label.status')">
							<template #text>
								<chip :color="participationStore.participation.status.color" small
									  :help="participationStore.participation.sub_status && participationStore.participation.sub_status.description ? participationStore.participation.sub_status.description : participationStore.participation.status.description">
									{{ participationStore.participation.status.title }}
								</chip>
							</template>
						</description-list-item>

						<description-list-item v-if="!participationStore.maxAttempts || participationStore.maxAttempts > 1" :title="$t('label.attempt')">
							<template #text>
								<chip small>
									{{ participationStore.participation.attempt }}
								</chip>
							</template>
						</description-list-item>

						<description-list-item v-if="participationStore.participation.starter_number" :title="$t('label.starter_number')">
							<template #text>
								<chip small>
									{{ participationStore.participation.starter_number }}
								</chip>
							</template>
						</description-list-item>

						<description-list-item :title="$t('label.participation_type')">
							<template #text>
								<div v-if="participationStore.participation.private" class="flex items-center">
									<span class="text-warning">{{ $t('label.anonymous') }}</span>
									<tooltip v-if="participationStore.participation.private" :text="$t('text.participation.private')" class="ml-4">
										<icon :icon="icons.anonymous" x-small color="warning"/>
									</tooltip>
								</div>
								<span v-else class="text-success">{{ $t('label.public') }}</span>
							</template>
						</description-list-item>

						<description-list-item :title="$t('label.participant_id')" :text="participationStore.participation.participant_id"/>

						<description-list-item :title="$t('label.registration_date')">
							<template #text>
								<dynamic-date :date="participationStore.participation.created_at" short time/>
							</template>
						</description-list-item>
						<description-list-item v-if="participationStore.participation.started_at" :title="$t('label.participated_at')">
							<template #text>
								<dynamic-date :date="participationStore.participation.started_at" short time/>
							</template>
						</description-list-item>
					</description-list>
				</card-text>
			</card>

		</card>

		<client-only>
			<template v-if="participationStore.canViewSensitiveInformation">
				<card xs-tile sm-tile>
					<card-title>{{ $t('label.protected_section') }}</card-title>
					<card-subtitle>{{ $t('text.protected_sections') }}</card-subtitle>

					<card id="advanced" v-if="licenseNumberLabel || participationStore.authPermissions.inputs.length || participationStore.authPermissions.participant_info || participationStore.authPermissions.register_info"
						  tile flat no-spacing class="divided">
						<card-title>
							<div class="w-full flex justify-between items-center">
								<span>{{ $t('label.advanced_information') }}</span>
								<nuxt-link v-if="participationStore.editable" class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}"
										   :to="$web('events.participations.edit', [eventStore.event._key, participationStore.participation._key])">{{ $t('action.edit') }}
								</nuxt-link>
							</div>
						</card-title>
						<card-text>
							<description-list>

								<description-list-item v-if="licenseNumberLabel" :title="licenseNumberLabel" :text="participationStore.authPermissions.license_number ?? '-'"/>

								<template v-if="participationStore.authPermissions.inputs.length">
									<template v-for="(input, inputKey) in participationStore.authPermissions.inputs" :key="inputKey">
										<description-list-item :title="input.label">
											<template #text>
												<dynamic-input-value :input="input" :value="input.value"/>
											</template>
										</description-list-item>
									</template>
								</template>

								<description-list-item v-if="participationStore.authPermissions.participant_info"
													   :title="$t('label.participant_info')"
													   :text="participationStore.authPermissions.participant_info" with-links/>
								<description-list-item v-if="participationStore.authPermissions.register_info" :title="$t('label.register_info')"
													   :text="participationStore.authPermissions.register_info"
													   with-links/>
							</description-list>
						</card-text>
					</card>

					<card id="products" v-if="eventStore.event.has_managed_sign_up && participationStore.participation.has_purchasable_products" tile flat no-spacing class="divided">
						<card-title>
							<div class="w-full flex justify-between items-center">
								<span>{{ $t('label.participation_products') }}</span>
								<nuxt-link v-if="participationStore.editable" class="text-sm font-regular font-text flex items-center" :style="{color: eventStore.colorStyle}"
										   :to="$web('events.participations.show.products', [eventStore.event._key, participationStore.participation._key])">
									<icon :icon="icons.create" x-small class="mr-4"/>
									<span>{{ $t('action.purchase.create') }}</span>
								</nuxt-link>
							</div>
						</card-title>
						<fetch-loader v-if="productPurchasesPending || productPurchasesError" :pending="productPurchasesPending" :error="productPurchasesError" @retry="refreshProductPurchases" fill/>
						<card-text v-else>
							<missing-info v-if="(!productPurchases || !productPurchases.length)" :text="$t('missing.participations.products')"/>
							<template v-else>
								<event-product-purchase v-for="purchase in productPurchases" :purchase="purchase" :event="eventStore.event" :key="purchase.id" outlined>
									<template #buttons>
										<btn v-if="purchase.order && purchase.order.status.value === 'failed'"
											 :to="$web('orders.pay', purchase.order._key)" color="success">
											<span>{{ $t('action.pay_now') }}</span>
											<icon :icon="icons.next"/>
										</btn>

										<btn v-if="participationStore.editable" @click="showContactOrganizerModalVisible(4)" small>
											<icon :icon="icons.edit" />
											<span>{{ $t('action.purchase.request_edit') }}</span>
										</btn>
									</template>
								</event-product-purchase>
							</template>
						</card-text>
					</card>

					<card id="contact" v-if="participationStore.authPermissions.contact" tile flat no-spacing class="divided">
						<card-title>
							<div class="w-full flex justify-between items-center">
								<span>{{ $t('label.contact_of_name', {name: participationStore.authPermissions.first_name}) }}</span>
								<nuxt-link v-if="participationStore.editable" class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}"
										   :to="$web('events.participations.edit', [eventStore.event._key, participationStore.participation._key])">{{ $t('action.edit') }}
								</nuxt-link>
							</div>
						</card-title>
						<card-text>
							<description-list>
								<description-list-item v-if="participationStore.authPermissions.contact.email" :title="$t('label.email')">
									<template #text>
									<span>
									<email :email="participationStore.authPermissions.contact.email" :color="eventStore.colorStyle"/>
									</span>
									</template>
								</description-list-item>
								<description-list-item v-if="participationStore.authPermissions.contact.phone" :title="$t('label.phone')" :text="participationStore.authPermissions.contact.phone"/>
								<description-list-item v-if="participationStore.authPermissions.contact.emergency_phone" :title="$t('label.emergency_phone')" :text="participationStore.authPermissions.contact.emergency_phone"/>
								<description-list-item v-if="participationStore.authPermissions.contact.full_address" :title="$t('label.address')"
													   :text="participationStore.authPermissions.contact.full_address"/>
							</description-list>
						</card-text>
					</card>

					<card v-if="!participationStore.participation.is_manual" id="documents" tile flat no-spacing class="divided">
						<card-title>{{ $t('label.documents') }}</card-title>
						<card-text>
							<template v-if="participationStore.authPermissions.certificates?.length">
								<document-item v-for="certificate in participationStore.authPermissions.certificates" :key="`certificate-${certificate.id}`"
											   @success="url => handleDownload(url, 'participation_certificate', certificate.template.name)"
											   with-error
											   :data="{token: participationStore.participationToken}"
											   :action="$api('events.participations.certificates.download', [eventStore.event._key, participationStore.participation._key, certificate._key])"
											   :title="certificate.template.name"
											   :description="certificate.template.description" :icon="icons.certificate">
									<template #icon>
										<cdn-image v-if="certificate.template.preview" :size="{ratio: 1/1.4142, width: 24}" :src="certificate.template.preview"/>
									</template>
								</document-item>
							</template>

							<document-item @success="participationStore.showCheckinCodeModal"
										   :title="$t('label.checkin_code')"
										   :description="$t('text.participation.checkin_code')" :icon="icons.code"
										   :data="{size: 240, token: participationStore.participationToken}" :action="$api('events.participations.code', [eventStore.event._key, participationStore.participation._key])"/>

							<document-item v-if="participationStore.participation.starter_number && participationStore.participation.has_starter_number_print_template"
										   @success="url => handleDownload(url, 'participation_starter_number')"
										   with-error
										   :data="{token: participationStore.participationToken}"
										   :action="$api('events.participations.downloads.starter_number', [eventStore.event._key, participationStore.participation._key])"
										   :title="$t('label.starter_number')"
										   :description="$t('text.participation.download_starter_number')" :icon="icons.starter_number"/>

							<document-item @success="url => handleDownload(url, 'participation_confirmation')"
										   with-error
										   :data="{token: participationStore.participationToken}"
										   :action="$api('events.participations.downloads.confirmation', [eventStore.event._key, participationStore.participation._key])"
										   :title="$t('label.registration_confirmation')"
										   :description="$t('text.participation.download_confirmation')" :icon="icons.confirmation"/>
						</card-text>
					</card>

					<card id="order" v-if="participationStore.authPermissions.order" tile flat no-spacing class="divided">
						<card-title>
							<div class="w-full flex justify-between items-center">
								<span>{{ $t('label.registration') }} / {{ $t('label.payment') }}</span>
								<nuxt-link v-if="participationStore.editable" class="text-sm font-regular font-text" :style="{color: eventStore.colorStyle}"
										   :to="$web('events.participations.show.payment', [eventStore.event._key, participationStore.participation._key])">{{ $t('action.show_all') }}
								</nuxt-link>
							</div>
						</card-title>
						<card-text>
							<lazy-order-card :order="participationStore.authPermissions.order" :colorStyle="eventStore.colorStyle" :with-permissions="participationStore.authPermissions.is_creator" outlined/>
						</card-text>
					</card>

					<card id="help" v-if="eventStore.event.is_managed_by_organizer && eventStore.event.club && participationStore.editable" tile flat no-spacing class="divided">
						<card-title>{{ $t('label.help_and_contact') }}</card-title>
						<card-text>
							<description-list class="mb-8">
								<description-list-item :title="$t('label.organizer')">
									<template #text>
										<nuxt-link :to="$web('clubs.show', eventStore.event.club._key)" :style="{color: eventStore.colorStyle}">
											{{ eventStore.event.club.display_name }}
										</nuxt-link>
									</template>
								</description-list-item>
							</description-list>

							<alert :content="$t('text.participation.contact_support')"/>
							<wrapper break-column="sm">
								<btn :to="$web('events.participations.edit', [eventStore.event._key, participationStore.participation._key])">
									<icon :icon="icons.edit"/>
									<span>{{ $t('action.edit_participation_data') }}</span>
								</btn>
								<btn @click="showContactOrganizerModalVisible(0)">
									<icon :icon="icons.contact"/>
									<span>{{ $t('action.contact_organizer') }}</span>
								</btn>
							</wrapper>
						</card-text>
					</card>
				</card>

				<lazy-modal v-if="participationStore.canViewSensitiveInformation && participationStore.participation.team" :title="`${$t('action.invite')}: ${participationStore.participation.team.name}`" v-model="isTeamInviteModalVisible">
					<div v-if="!participationStore.participation.team.public">
						<alert type="warning" :content="$t('text.team.private', {team: participationStore.participation.team.name})"/>
						<p class="mb-0 text-lg">{{ $t('label.join_code') }}: <b>{{ participationStore.authPermissions.team_join_code }}</b></p>
					</div>
					<alert v-else type="success" :content="$t('text.team.public', {team: participationStore.participation.team.name})"/>
				</lazy-modal>

				<lazy-modal v-if="eventStore.event.is_managed_by_organizer && participationStore.editable" v-model="isContactOrganizerModalVisible" :title="$t('action.contact_organizer')">
					<support-form :event="eventStore.event" :email="eventStore.event.email" :phone="eventStore.event.phone" :data="contactOrganizerData" @success="isContactOrganizerModalVisible = false"/>
				</lazy-modal>
			</template>
		</client-only>
	</div>
</template>

<script setup lang="ts">

import {mdiFileCertificateOutline, mdiFileDocumentOutline, mdiMedalOutline, mdiNumeric, mdiQrcode} from '@mdi/js';
import {useParticipationStore} from "../../../../../store/participation";
import {useEventStore} from "../../../../../store/event";
import {anonymousIcon, createIcon, editIcon, helpIcon, nextIcon, warningIcon} from "@spoferan/nuxt-spoferan/icons";
import {getSportTypeLicenseNumberLangKey, ModelType, ParticipationClassRankMode, ParticipationInputState} from "@spoferan/spoferan-ts-core";

const eventStore = useEventStore();
const participationStore = useParticipationStore();
const {$analytics, $downloadFile, $api, $auth, $date} = useNuxtApp();
const {t} = useI18n();
const router = useRouter();

const {
	reactiveData: productPurchases,
	error: productPurchasesError,
	pending: productPurchasesPending,
	refresh: refreshProductPurchases
} = participationStore.participation.has_purchasable_products ? (await useApiFetch($api('events.participations.purchases.index', [eventStore.event.id, participationStore.participation.id]), {
	auth: true,
	params: {
		relations: ['product', 'order', 'inputs'],
	}
})) : {reactiveData: ref([]), error: null, pending: false, refresh: null};

const {
	reactiveData: participationPolyline,
	pending: participationPolylinePending,
	error: participationPolylineError,
	refresh: refreshParticipationPolyline
} = participationStore.hasStarted && (!participationStore.hasSensitiveTracking || !participationStore.participation.private) && participationStore.participation.participationClass && (participationStore.participation.route || (!participationStore.hasFinished && (participationStore.participation.participationClass.rank_mode === ParticipationClassRankMode.App || participationStore.participation.participationClass.live_tracking_enabled))) ? (await useApiFetch(`/v1/events/${eventStore.event.id}/participations/${participationStore.participation.id}/track`, {
	lazy: true,
	server: false,
	guest: true,
	params: {
		ts: participationStore.hasFinished ? participationStore.participation.cache_buster : null
	}
})) : {reactiveData: ref(null), error: null, pending: false, refresh: null};

const safeBoundingDistanceInMeters = 400;
const safeParticipationPolyline = computed(() => {
	if (!participationStore.hasSensitiveTracking || !participationPolyline.value?.length) {
		return participationPolyline.value;
	}

	const safeParticipationPolylineStartIndex = participationPolyline.value.findIndex(trackSpot => trackSpot.distance_from_start >= safeBoundingDistanceInMeters);
	const finalDistance = participationPolyline.value[participationPolyline.value.length - 1].distance_from_start;
	const safeFinishDistance = finalDistance - safeBoundingDistanceInMeters;

	const safeParticipationPolylineEndIndex = Math.max(participationPolyline.value.findIndex(trackSpot => trackSpot.distance_from_start >= safeFinishDistance, safeParticipationPolylineStartIndex.value));

	return participationPolyline.value.slice(safeParticipationPolylineStartIndex, safeParticipationPolylineEndIndex);
});

const navigatingToTrackingPage = ref(false);

const icons = {
	code: mdiQrcode,
	certificate: mdiFileCertificateOutline,
	starter_number: mdiNumeric,
	confirmation: mdiFileDocumentOutline,
	warning: warningIcon,
	edit: editIcon,
	contact: helpIcon,
	next: nextIcon,
	create: createIcon,
	anonymous: anonymousIcon,
	rank: mdiMedalOutline,
};

const isTeamInviteModalVisible = ref(false);

const isContactOrganizerModalVisible = ref(false);
const contactOrganizerData = ref({});

const liveTime = ref(null);
const liveTimeDate = ref(null);
let rankTimeRefreshInterval = null;

const licenseNumberLabel = participationStore.authPermissions.license_number_input !== ParticipationInputState.Disabled ? t(`label.license_number.${getSportTypeLicenseNumberLangKey(participationStore.participation.participationClass.sport_type_id)}`) : null;

const {getColorForRank} = useColorHelpers();

function handleDownload(url, type, label = null) {
	$analytics.trackEvent('download', {model_type_name: type,
		model_name: label,
		model_key: participationStore.participation._key,
		url: url
	});

	$downloadFile(url);
}


function showContactOrganizerModalVisible(type: number | null = null) {
	contactOrganizerData.value = {
		message: null,
		participation_id: participationStore.participation.id,
		event_id: eventStore.event.id,
		type: type,
		email: $auth.user ? $auth.user.email : null,
		name: $auth.user ? $auth.user.display_name : null
	}
	isContactOrganizerModalVisible.value = true
}

function navigateToTrackingPage() {
	// As the tracking page might take some time to load depending on the amount of map elements,
	// we display a loading state to the user to prevent him from clicking the button multiple times
	navigatingToTrackingPage.value = true;
	router.push(`/events/${eventStore.event._key}/participations/${participationStore.participation._key}/tracking`);
}

function refreshLiveValues() {
	if (!eventStore.isCompetition || !participationStore.participation || !participationStore.participation.started_at) {
		return;
	}

	// In case the update was triggered before
	if (rankTimeRefreshInterval) {
		clearInterval(rankTimeRefreshInterval);
	}

	rankTimeRefreshInterval = setInterval(() => {
		if (!participationStore.isActive) {
			if (rankTimeRefreshInterval) {
				clearInterval(rankTimeRefreshInterval);
			}
			liveTime.value = null;
			liveTimeDate.value = null;
		} else {
			if (!liveTimeDate.value || !liveTime.value) {
				liveTimeDate.value = $date().time($date().timeDiff(participationStore.participation.started_at))
				liveTime.value = liveTimeDate.value.format('HH:mm:ss');
			} else {
				liveTimeDate.value = liveTimeDate.value.add(1, 'second');
				liveTime.value = liveTimeDate.value.format('HH:mm:ss');
			}
		}
	}, 1000);
}

onMounted(() => {
	if (participationStore.isActive) {
		refreshLiveValues();
	}
})

onBeforeUnmount(() => {
	if (rankTimeRefreshInterval) {
		clearInterval(rankTimeRefreshInterval);
	}
})

watch(() => participationStore.isActive, () => {
	refreshLiveValues();
});

</script>

